import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button } from '@mui/material';
import type { AxiosError } from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useLocation } from 'react-router';

import ExportButtonV2 from '@/components/ExportButtonV2';
import { triggerFileDownload } from '@/components/ExportButtonV2/ExportButtonV2.utils';
import type { QBFilterOptions } from '@/components/QueryBuilder';
import useQueryBuilderParams from '@/components/QueryBuilder/QueryBuilder.hooks';
import QueryBuilderBar from '@/components/QueryBuilder/QueryBuilderBar';
import {
  CASES_FILTERS_STORAGE_KEY,
  INTERNAL_CASES_FILTERS_STORAGE_KEY
} from '@/constants/common';
import CaseTable from '@/features/CaseTable';
import {
  mergeSearchableSelectFieldOptions,
  mergeSearchableSelectOptions
} from '@/features/SearchBar/SearchBar.utils';
import useHandleError from '@/hooks/useHandleError';
import Header from '@/layout/Header/Header';
import MainContent from '@/layout/MainContent';
import queryClient from '@/services/BaseApi.service';

import { selectedOrganizationAtom } from '../Organizations';
import { mapFiltersToRequestParams } from '../Tickets/Tickets.store';
import {
  CASE_SEARCHABLE_SELECT_FILTERS,
  CASES_GROUP_BY_FIELDS,
  INTERNAL_CASE_SEARCHABLE_SELECT_FILTERS
} from './Cases.constants';
import {
  casesFiltersAtom,
  casesGridApiAtom,
  casesTimeFilterAtom,
  internalCasesFiltersAtom,
  internalCasesGridApiAtom,
  internalCasesTimeFilterAtom,
  useFetchCaseExport,
  useFetchCaseFilters,
  useFetchCaseGroupByOptions
} from './Cases.store';
import useStyles from './Cases.styles';
import { mapCaseFiltersToRequestParams } from './Cases.utils';

const Cases = () => {
  const [caseFilters, setCaseFilters] = useAtom(casesFiltersAtom);
  const [caseTimeFilter, setCaseTimeFilter] = useAtom(casesTimeFilterAtom);
  const [caseInternalFilters, setCaseInternalFilters] = useAtom(
    internalCasesFiltersAtom
  );
  const [caseInternalTimeFilter, setCaseInternalTimeFilter] = useAtom(
    internalCasesTimeFilterAtom
  );
  const [casesGridApi] = useAtom(casesGridApiAtom);
  const [internalCasesGridApi] = useAtom(internalCasesGridApiAtom);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const { classes } = useStyles();
  const { pathname } = useLocation();
  const isInternalCasePage = pathname.includes('/cases-internal');
  const { mutateAsync: fetchCaseExport } = useFetchCaseExport();
  const handleError = useHandleError();

  const [pageCaseFilters, pageSetCaseFilters] = isInternalCasePage
    ? [caseInternalFilters, setCaseInternalFilters]
    : [caseFilters, setCaseFilters];

  const [pageCaseTimeFilter, pageSetCaseTimeFilter] = isInternalCasePage
    ? [caseInternalTimeFilter, setCaseInternalTimeFilter]
    : [caseTimeFilter, setCaseTimeFilter];

  const pageFilterStorageKey = isInternalCasePage
    ? INTERNAL_CASES_FILTERS_STORAGE_KEY
    : CASES_FILTERS_STORAGE_KEY;

  const { data: caseFiltersConfig, isLoading: isLoadingCaseFilters } =
    useFetchCaseFilters();

  const { isReady } = useQueryBuilderParams({
    config: caseFiltersConfig || {},
    isLoading: isLoadingCaseFilters,
    sessionKey: `${pageFilterStorageKey}V2`,
    normalizeFilters: (filters) => filters,
    defaultFilter: [],
    setFilters: pageSetCaseFilters,
    setTimeFilter: pageSetCaseTimeFilter
  });

  const requestParams = mapCaseFiltersToRequestParams(
    pageCaseFilters || [],
    pageCaseTimeFilter
  );

  const { data: groupByOptions } = useFetchCaseGroupByOptions(
    CASES_GROUP_BY_FIELDS,
    requestParams,
    undefined,
    isReady
  );

  const qbOptions = useMemo(
    () =>
      mergeSearchableSelectOptions(groupByOptions || {}, {}) as Record<
        string,
        QBFilterOptions[]
      >,

    [groupByOptions]
  );

  const casesSearchableFilters = useMemo(() => {
    if (!caseFiltersConfig) return [];

    const pageAdjustedSearchableSelectFilters = isInternalCasePage
      ? INTERNAL_CASE_SEARCHABLE_SELECT_FILTERS
      : CASE_SEARCHABLE_SELECT_FILTERS;

    const searchableSelectFilters = pageAdjustedSearchableSelectFilters.map(
      (f) => {
        const filter = { ...f };
        const { field } = filter;
        let { filterOptions } = filter;

        if (qbOptions[field]) {
          filterOptions = mergeSearchableSelectFieldOptions(
            filter.filterOptions,
            qbOptions[field] as QBFilterOptions[]
          );
        }

        // if options are defined in the constants, ensure the sort order is maintained
        if (filter.filterOptions.length > 0) {
          const sortingValues = filter.filterOptions.map(
            (option) => option.value
          );

          filterOptions = filterOptions.sort((a, b) =>
            sortingValues.indexOf(a.value) > sortingValues.indexOf(b.value)
              ? 1
              : -1
          );
        }

        return {
          ...filter,
          filterOptions
        };
      }
    );

    return searchableSelectFilters;
  }, [qbOptions, caseFiltersConfig]);

  const gridApi = isInternalCasePage ? internalCasesGridApi : casesGridApi;
  const refetch = () => {
    queryClient.removeQueries({ queryKey: ['v2/cases/search'] });
    gridApi?.refreshServerSide({ purge: true });
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const mappedFilters = mapFiltersToRequestParams(
        pageCaseFilters || [],
        pageCaseTimeFilter
      );

      if (selectedOrganization.id > 0) {
        mappedFilters.organization__label = selectedOrganization?.label || '';
      }

      const response = await fetchCaseExport({
        format,
        caseFilters: mappedFilters
      });

      const mimeType =
        format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json';
      const blob = new Blob([response.data], { type: mimeType });
      triggerFileDownload(blob, format, 'cases-export');
    } catch (error) {
      handleError(error as AxiosError, 'Failure to export cases.');
    }
  };

  return (
    <>
      <Header
        title="Cases"
        actions={
          <>
            <ExportButtonV2 handleClick={handleExport} json csv />
            <Button onClick={() => refetch()} variant="outlined">
              <FontAwesomeIcon size="lg" icon={faRefresh} />
            </Button>
          </>
        }
      >
        <Box sx={{ px: 3, pb: 2 }}>
          {caseFiltersConfig && (
            <QueryBuilderBar
              enableTimeFilter
              config={caseFiltersConfig}
              options={qbOptions}
              sessionKey={`${pageFilterStorageKey}V2`}
              searchableSelectFilters={casesSearchableFilters}
              filters={pageCaseFilters || []}
              timeFilter={pageCaseTimeFilter}
            />
          )}
        </Box>
      </Header>
      <MainContent>
        <Box className={classes.casesPage}>{isReady && <CaseTable />}</Box>
      </MainContent>
    </>
  );
};

export default Cases;
